import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import "./GitHubStats.css";

const LANG_COLORS = {
  JavaScript: "#f1e05a",
  TypeScript: "#3178c6",
  Python: "#3572A5",
  HTML: "#e34c26",
  CSS: "#563d7c",
  "C#": "#178600",
  Java: "#b07219",
  Vue: "#41b883",
  Go: "#00ADD8",
  Rust: "#dea584",
  Shell: "#89e051",
  default: "#8b949e",
};

function useCountUp(target, active, duration = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active || target == null) return;
    let startTs = null;
    let raf;
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return value;
}

const USERNAME = "Mjosuex85";
const PROD_API_ORIGIN = "https://portfoliomv.vercel.app";
const COLORS = ["#161b22", "#0e4429", "#006d32", "#26a641", "#39d353"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function getColor(count) {
  if (count === 0) return COLORS[0];
  if (count <= 3) return COLORS[1];
  if (count <= 6) return COLORS[2];
  if (count <= 9) return COLORS[3];
  return COLORS[4];
}

function getMonthLabels(weeks) {
  const labels = [];
  let lastMonth = -1;
  weeks.forEach((week, wi) => {
    if (!week.contributionDays?.length) return;
    const month = new Date(week.contributionDays[0].date).getMonth();
    if (month !== lastMonth) {
      labels.push({ weekIndex: wi, month });
      lastMonth = month;
    }
  });
  return labels;
}

const DAY_LABELS = ["", "Mon", "", "Wed", "", "Fri", ""];

function GitHubStats() {
  const es_en = useSelector((state) => state.translation);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const endpoints = ["/api/github-stats"];

    if (window.location.hostname === "localhost") {
      endpoints.push(`${PROD_API_ORIGIN}/api/github-stats`);
    }

    const loadStats = async () => {
      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            headers: { Accept: "application/json" },
          });

          if (!response.ok) {
            continue;
          }

          const contentType = response.headers.get("content-type") || "";
          if (!contentType.includes("application/json")) {
            continue;
          }

          const payload = await response.json();
          if (payload.error) {
            continue;
          }

          setData(payload);
          setError(null);
          return;
        } catch {
          // Try the next endpoint.
        }
      }

      setError("Failed to load");
    };

    loadStats();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.15 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const active = inView && !!data;
  const cContributions = useCountUp(data?.totalContributions, active);
  const cCommits = useCountUp(data?.totalCommits, active);
  const cPRs = useCountUp(data?.totalPRs, active);
  const cIssues = useCountUp(data?.totalIssues, active);
  const cRepos = useCountUp(data?.totalRepos, active);
  const cStars = useCountUp(data?.totalStars, active);

  const totalLangRepos = data
    ? data.topLangs.reduce((s, l) => s + l.count, 0)
    : 1;

  return (
    <section className="githubstats section" id="github" ref={sectionRef}>
      <h2 className="section__title">GitHub</h2>
      <span className="section__subtitle">
        {es_en ? "My activity" : "Mi actividad"}
      </span>

      <div className="githubstats__container container">
        {error && (
          <p className="githubstats__error">
            {es_en
              ? "Could not load GitHub data"
              : "No se pudieron cargar los datos de GitHub"}
          </p>
        )}

        {!data && !error && (
          <p className="githubstats__loading">
            {es_en ? "Loading..." : "Cargando..."}
          </p>
        )}

        {data && (
          <>
            {/* Stats cards - GitHub style */}
            <div className="githubstats__numbers">
              <div className="githubstats__stat">
                <i className="uil uil-chart-bar githubstats__stat-icon"></i>
                <span className="githubstats__stat-value">
                  {cContributions}
                </span>
                <span className="githubstats__stat-label">
                  {es_en ? "Contributions" : "Contribuciones"}
                </span>
              </div>
              <div className="githubstats__stat">
                <i className="uil uil-github githubstats__stat-icon"></i>
                <span className="githubstats__stat-value">{cCommits}</span>
                <span className="githubstats__stat-label">Commits</span>
              </div>
              <div className="githubstats__stat">
                <i className="uil uil-code-branch githubstats__stat-icon"></i>
                <span className="githubstats__stat-value">{cPRs}</span>
                <span className="githubstats__stat-label">Pull Requests</span>
              </div>
              <div className="githubstats__stat">
                <i className="uil uil-comment-alt githubstats__stat-icon"></i>
                <span className="githubstats__stat-value">{cIssues}</span>
                <span className="githubstats__stat-label">Issues</span>
              </div>
              <div className="githubstats__stat">
                <i className="uil uil-folder githubstats__stat-icon"></i>
                <span className="githubstats__stat-value">{cRepos}</span>
                <span className="githubstats__stat-label">
                  {es_en ? "Repositories" : "Repositorios"}
                </span>
              </div>
              <div className="githubstats__stat">
                <i className="uil uil-star githubstats__stat-icon"></i>
                <span className="githubstats__stat-value">{cStars}</span>
                <span className="githubstats__stat-label">Stars</span>
              </div>
            </div>

            {/* Top languages */}
            <div className="githubstats__langs">
              {data.topLangs.map((lang) => {
                const pct = ((lang.count / totalLangRepos) * 100).toFixed(1);
                const color = LANG_COLORS[lang.name] || LANG_COLORS.default;
                return (
                  <div key={lang.name} className="githubstats__lang-row">
                    <span className="githubstats__lang-name">
                      <span
                        className="githubstats__lang-dot"
                        style={{ background: color }}
                      />
                      {lang.name}
                    </span>
                    <div className="githubstats__lang-bar-wrap">
                      <div
                        className="githubstats__lang-bar-fill"
                        style={{
                          width: active ? `${pct}%` : "0%",
                          background: color,
                        }}
                      />
                    </div>
                    <span className="githubstats__lang-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>

            {/* Contribution calendar */}
            <div className="githubstats__graph-card">
              <div className="githubstats__graph-header">
                <span className="githubstats__graph-title">
                  {data.totalContributions}{" "}
                  {es_en
                    ? "contributions in the last year"
                    : "contribuciones en el último año"}
                </span>
                <a
                  href={`https://github.com/${USERNAME}`}
                  target="_blank"
                  rel="noreferrer"
                  className="githubstats__profile-link"
                >
                  {es_en ? "View profile" : "Ver perfil"} →
                </a>
              </div>

              <div className="githubstats__calendar-wrap">
                {/* Month labels */}
                <div className="githubstats__month-row">
                  <div className="githubstats__day-spacer"></div>
                  <div className="githubstats__months">
                    {getMonthLabels(data.weeks).map(({ weekIndex, month }) => (
                      <span
                        key={weekIndex}
                        className="githubstats__month-label"
                        style={{ gridColumnStart: weekIndex + 1 }}
                      >
                        {MONTHS[month]}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="githubstats__grid-row">
                  <div className="githubstats__day-labels">
                    {DAY_LABELS.map((label, i) => (
                      <span key={i} className="githubstats__day-label">
                        {label}
                      </span>
                    ))}
                  </div>
                  <div className="githubstats__calendar">
                    {data.weeks.map((week, wi) => (
                      <div key={wi} className="githubstats__week">
                        {week.contributionDays.map((day) => (
                          <div
                            key={day.date}
                            className="githubstats__cell"
                            style={{
                              background: getColor(day.contributionCount),
                            }}
                            title={`${day.date}: ${day.contributionCount} contribution${day.contributionCount !== 1 ? "s" : ""}`}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="githubstats__legend">
                  <span className="githubstats__legend-label">
                    {es_en ? "Less" : "Menos"}
                  </span>
                  {COLORS.map((c, i) => (
                    <div
                      key={i}
                      className="githubstats__legend-cell"
                      style={{ background: c }}
                    />
                  ))}
                  <span className="githubstats__legend-label">
                    {es_en ? "More" : "Más"}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default GitHubStats;
